import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { slides } from './src/data/slides';
import { SlideRenderer } from './src/components/SlideRenderer';
const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 40 : -40,
    opacity: 0
  }),
  center: {
    zIndex: 1,
    x: 0,
    opacity: 1
  },
  exit: (direction: number) => ({
    zIndex: 0,
    x: direction < 0 ? 40 : -40,
    opacity: 0
  })
};
export function App() {
  const [[page, direction], setPage] = useState([0, 0]);
  const currentSlideIndex = Math.max(0, Math.min(page, slides.length - 1));
  const currentSlide = slides[currentSlideIndex];
  const paginate = useCallback((newDirection: number) => {
    setPage(([currentPage]) => {
      const nextIndex = currentPage + newDirection;
      if (nextIndex < 0 || nextIndex >= slides.length) return [currentPage, 0];
      return [nextIndex, newDirection];
    });
  }, []);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'Space') {
        paginate(1);
      } else if (e.key === 'ArrowLeft') {
        paginate(-1);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [paginate]);
  return <div className="min-h-screen w-full bg-white text-slate-900 font-['Inter'] flex flex-col overflow-hidden selection:bg-[#6C2BD9]/20 relative" data-id="element-0">
      {/* Main Slide Area */}
      <div className="flex-1 relative" data-id="element-1">
        <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-2">
          <motion.div key={page} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
          x: {
            type: 'spring',
            stiffness: 300,
            damping: 30
          },
          opacity: {
            duration: 0.3
          }
        }} className="absolute inset-0 w-full h-full flex items-center justify-center py-20" data-id="element-3">
            <SlideRenderer slide={currentSlide} data-id="element-4" />
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Bottom Navigation UI */}
      <div className="absolute bottom-0 left-0 w-full p-6 flex items-center justify-between z-50 pointer-events-none" data-id="element-5">
        {/* Progress / Counter */}
        <div className="text-sm font-medium text-slate-400 tracking-widest" data-id="element-6">
          {String(currentSlideIndex + 1).padStart(2, '0')} / {slides.length}
        </div>

        {/* Controls */}
        <div className="flex items-center gap-2 pointer-events-auto" data-id="element-7">
          <button onClick={() => paginate(-1)} disabled={currentSlideIndex === 0} className="p-2 rounded-full text-slate-400 hover:text-[#6C2BD9] hover:bg-slate-50 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400 transition-colors" aria-label="Previous slide" data-id="element-8">
            <ChevronLeft size={24} data-id="element-9" />
          </button>
          <button onClick={() => paginate(1)} disabled={currentSlideIndex === slides.length - 1} className="p-2 rounded-full text-slate-400 hover:text-[#6C2BD9] hover:bg-slate-50 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400 transition-colors" aria-label="Next slide" data-id="element-10">
            <ChevronRight size={24} data-id="element-11" />
          </button>
        </div>
      </div>
    </div>;
}